import styles from './index.less';
import * as React from 'react';
import { observer } from 'mobx-react';
import useStore from '@src/utils/hooks/use-store';
import Page5Store from '@src/pages/page5/store';
import Page from '@src/layout-components/page';
import $$tracker from '@src/tracker';
import { GAME_OFFICIAL_WEBSITE } from '@src/constants/urls';
import HeaderActions from '@src/ui-components/header-actions';

const designImage = require('./images/page5-design-base.jpg');
const neutralSideNavImage = require('./images/page5-side-nav-neutral.png');
const neutralFirstTabImage = require('./images/page5-first-tab-neutral.png');
const sideNavImages = [
  require('./images/page5-nav-item-1.png'),
  require('./images/page5-nav-item-2.png'),
  require('./images/page5-nav-item-3.png'),
  require('./images/page5-nav-item-4.png'),
  require('./images/page5-nav-item-5.png'),
  require('./images/page5-nav-item-6.png'),
  require('./images/page5-nav-item-7.png'),
  require('./images/page5-nav-item-8.png'),
  require('./images/page5-nav-item-9.png'),
  require('./images/page5-nav-item-10.png'),
  require('./images/page5-nav-item-11.png')
];
const expandButtonImage = require('./images/page5-design-side-nav-expand.png');
const collapseButtonImage = require('./images/page5-design-side-nav-collapse.png');

const DESIGN_WIDTH = 1920;
const DESIGN_HEIGHT = 11878;
const SCROLL_THRESHOLD = 700;
const isMobileViewport = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const screenWidth = window.screen && window.screen.width;
  return (
    screenWidth < 450 ||
    screenWidth / (window.devicePixelRatio || 1) < 450 ||
    window.outerWidth < 450 ||
    window.innerWidth < 450 ||
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  );
};
const pageTabs = [
  { href: '#/', label: '返回首页', left: 415, width: 178 },
  { href: '#/page1', label: '铁浮城战 焕新重启', trackerTitle: '铁浮城战', left: 593, width: 166 },
  { href: '#/page2', label: '天王觉醒 再耀锋芒', trackerTitle: '天王觉醒', left: 759, width: 164 },
  { href: '#/page3', label: '珍材现世 打宝赚金', trackerTitle: '珍材现世', left: 923, width: 166 },
  { href: '#/page4', label: '十八周年 盛馈江湖', trackerTitle: '十八周年', left: 1089, width: 166 },
  { href: '#/page5', label: '周年特献 首个生态服', trackerTitle: '周年特献', left: 1255, width: 205 }
];

const trackPageTab = tab => {
  if (tab.trackerTitle) {
    $$tracker.clickMenu(tab.trackerTitle);
  }
};

const sections = [
  { label: '最夯福利', top: 1368, left: 440, navTop: 158, titleImage: '20260928085902177.png' },
  { label: '领百万绑金', top: 1487, navTop: 228, titleImage: '20260928094205249.png' },
  { label: '月卡享输出加成', top: 1811, navTop: 267, titleImage: '20260928094348986.png' },
  { label: '送黄金套+6技同伴', top: 2616, navTop: 306, titleImage: '20260928094548415.png' },
  { label: '随机免单', top: 3072, navTop: 344, titleImage: '20260928094857400.png' },
  { label: '多重好礼', top: 3774, navTop: 381, titleImage: '20260928095018707.png' },
  { label: '降肝减负', top: 8323, left: 618, navTop: 432, titleImage: '20260928090000524.png' },
  { label: '畅爽无压', top: 9206, left: 792, navTop: 499, titleImage: '20260929090356311.png' },
  { label: '轻松变现', top: 9908, left: 971, navTop: 562, titleImage: '20260928090226991.png' },
  { label: '经典玩法', top: 10554, left: 1149, navTop: 625, titleImage: '20260928090313965.png' },
  { label: '超级人气', top: 10894, left: 1326, navTop: 688, titleImage: '20260928090331803.png' }
];

const toHorizontalPercent = value => `${(value / DESIGN_WIDTH) * 100}%`;
const toVerticalPercent = value => `${(value / DESIGN_HEIGHT) * 100}%`;

const Page5 = () => {
  const store = useStore(() => new Page5Store());
  const sectionRefs = React.useRef([]);
  const cmsContentRef = React.useRef(null);
  const [activeSection, setActiveSection] = React.useState(-1);
  const [isMobile, setIsMobile] = React.useState(isMobileViewport);
  const [isPastThreshold, setIsPastThreshold] = React.useState(false);
  const [isSideNavCollapsed, setIsSideNavCollapsed] = React.useState(false);

  React.useEffect(() => {
    const rootScrollBehavior = document.documentElement.style.scrollBehavior;
    const bodyScrollBehavior = document.body.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';
    document.body.style.scrollBehavior = 'auto';

    return () => {
      document.documentElement.style.scrollBehavior = rootScrollBehavior;
      document.body.style.scrollBehavior = bodyScrollBehavior;
    };
  }, []);

  const getSectionTargets = React.useCallback(
    () => {
      if (store.contentHtml && cmsContentRef.current) {
        // CMS inserts and renames anchors; match each visible title instead of its anchor position.
        return sections.map(section => cmsContentRef.current.querySelector(`img[src*="${section.titleImage}"]`));
      }
      return sectionRefs.current;
    },
    [store.contentHtml]
  );

  React.useEffect(
    () => {
      const onScroll = () => {
        setIsPastThreshold(window.pageYOffset > SCROLL_THRESHOLD);

        // Loading images can temporarily shift anchors; the hero must remain unselected.
        if (window.pageYOffset <= 0) {
          setActiveSection(-1);
          return;
        }

        let nextActive = -1;
        let firstVisibleAtBottom = -1;
        const isAtBottom = window.pageYOffset + window.innerHeight >= document.documentElement.scrollHeight - 1;
        getSectionTargets().forEach((section, index) => {
          if (!section) return;
          const top = section.getBoundingClientRect().top;
          if (top <= 1) nextActive = index;
          // At the end, retain the first visible heading instead of skipping to the last one.
          if (isAtBottom && firstVisibleAtBottom < 0 && top >= 0 && top < window.innerHeight) firstVisibleAtBottom = index;
        });
        if (firstVisibleAtBottom >= 0) nextActive = firstVisibleAtBottom;
        setActiveSection(nextActive);
      };

      onScroll();
      const onResize = () => {
        setIsMobile(isMobileViewport());
        onScroll();
      };
      const cmsContent = cmsContentRef.current;
      if (cmsContent) cmsContent.addEventListener('load', onScroll, true);
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onResize);
      return () => {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onResize);
        if (cmsContent) cmsContent.removeEventListener('load', onScroll, true);
      };
    },
    [getSectionTargets]
  );

  const setSectionRef = index => node => {
    sectionRefs.current[index] = node;
  };

  const scrollToSection = index => {
    const section = getSectionTargets()[index];
    if (!section) return;

    const top = section.getBoundingClientRect().top + window.pageYOffset;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const showSideNav = isMobile || (isPastThreshold && !isSideNavCollapsed);
  const showExpandButton = !isMobile && isPastThreshold && isSideNavCollapsed;
  const navPosition = (top, height) => ({ top: `${(top / 963) * 100}%`, height: `${(height / 963) * 100}%` });
  const sectionTargets = getSectionTargets();

  return (
    <Page className={styles.root}>
      <main className={styles.designCanvas}>
        <div className={styles.designBackdrop}>
          <HeaderActions onWebsiteClick={() => $$tracker.clickGoWebsite()} onDownloadClick={() => $$tracker.clickDownload()} />
          <a
            className={styles.officialLogo}
            href={GAME_OFFICIAL_WEBSITE}
            aria-label="剑侠世界官网"
            onClick={() => $$tracker.clickGoWebsite()}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className={styles.srOnly}>剑侠世界官网</span>
          </a>
          <img className={styles.designImage} src={designImage} alt="周年特献首个生态服活动资料" />
          {activeSection !== 0 ? (
            <img
              className={styles.neutralFirstTab}
              src={neutralFirstTabImage}
              alt=""
              aria-hidden="true"
              style={{ left: toHorizontalPercent(379), top: toVerticalPercent(1112), width: toHorizontalPercent(281) }}
            />
          ) : null}

          <nav className={styles.pageNav} aria-label="资料片页面导航">
            {pageTabs.map(tab => (
              <a
                href={tab.href}
                key={tab.href}
                style={{ left: toHorizontalPercent(tab.left), width: toHorizontalPercent(tab.width) }}
                aria-current={tab.href === '#/page5' ? 'page' : undefined}
                onClick={() => trackPageTab(tab)}
              >
                <span className={styles.srOnly}>{tab.label}</span>
              </a>
            ))}
          </nav>

          <nav className={styles.sectionNav} aria-label="内容章节">
            {sections.map(
              (section, index) =>
                section.left === undefined ? null : (
                  <button
                    type="button"
                    key={section.label}
                    style={{ left: toHorizontalPercent(section.left) }}
                    aria-current={activeSection === index ? 'location' : undefined}
                    onClick={() => scrollToSection(index)}
                  >
                    <span className={styles.srOnly}>{section.label}</span>
                  </button>
                )
            )}
          </nav>

          {sections.map((section, index) => (
            <span
              className={styles.sectionAnchor}
              key={section.label}
              ref={setSectionRef(index)}
              style={{ top: toVerticalPercent(section.top) }}
              aria-hidden="true"
            />
          ))}
        </div>

        {/* Official CMS HTML is trusted first-party publisher content. */}
        {store.contentHtml ? <section ref={cmsContentRef} className={styles.cmsContent} dangerouslySetInnerHTML={{ __html: store.contentHtml }} /> : null}
      </main>

      <aside className={`${styles.sideNav} ${showSideNav ? styles.isVisible : styles.isHidden}`} aria-label="页内导航" aria-hidden={!showSideNav}>
        <img src={activeSection < 0 ? neutralSideNavImage : sideNavImages[activeSection]} alt="" aria-hidden="true" />
        <button
          type="button"
          className={styles.collapseSideNav}
          style={navPosition(80, 60)}
          onClick={() => !isMobile && setIsSideNavCollapsed(true)}
          tabIndex={showSideNav ? 0 : -1}
        >
          <img src={collapseButtonImage} alt="" aria-hidden="true" />
          <span className={styles.srOnly}>收起菜单</span>
        </button>
        {sections.map((section, index) => (
          <button
            type="button"
            className={section.left === undefined ? styles.textSection : undefined}
            style={navPosition(section.navTop, section.left === undefined ? 38 : 64)}
            key={section.label}
            aria-current={activeSection === index ? 'location' : undefined}
            onClick={() => scrollToSection(index)}
            disabled={Boolean(store.contentHtml) && !sectionTargets[index]}
            tabIndex={showSideNav && (!store.contentHtml || sectionTargets[index]) ? 0 : -1}
          >
            <span className={styles.srOnly}>{section.label}</span>
          </button>
        ))}
        <button type="button" className={styles.backTop} style={navPosition(768, 88)} onClick={scrollToTop} tabIndex={showSideNav ? 0 : -1}>
          <span className={styles.srOnly}>返回顶部</span>
        </button>
        <a className={styles.backHome} href="#/" tabIndex={showSideNav ? 0 : -1} style={navPosition(867, 88)}>
          <span className={styles.srOnly}>返回首页</span>
        </a>
      </aside>

      <button
        type="button"
        className={`${styles.expandSideNav} ${showExpandButton ? styles.isVisible : styles.isHidden}`}
        onClick={() => setIsSideNavCollapsed(false)}
        tabIndex={showExpandButton ? 0 : -1}
        aria-hidden={!showExpandButton}
      >
        <img src={expandButtonImage} alt="" aria-hidden="true" />
        <span className={styles.srOnly}>展开菜单</span>
      </button>
    </Page>
  );
};

export default observer(Page5);
